import React, { useEffect, useMemo, useState } from 'react';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { ContextOption } from '../../services/emailTemplateService';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';

/**
 * Two-level context picker for email and SMS templates.
 *
 * The flat list had grown to every questionnaire listed twice - once as an
 * answers context and once as an invite context - plus the entities, which is
 * a long scroll to find one item in. Choosing the section first cuts it to a
 * handful either way, and it also makes the distinction between "about this
 * questionnaire" and "inviting somebody to it" visible, which the flat list
 * buried in a label prefix.
 *
 * Groups come from the server (ContextOption.group). An option that arrives
 * without one falls into "Other" rather than disappearing - a context the
 * picker cannot show is a template nobody can edit.
 */

const FALLBACK_GROUP = 'Other';

//: The order sections are offered in. Anything the server sends that is not
//: listed here is appended, so a new group needs no change on this side.
const GROUP_ORDER = ['Entities', 'Questionnaires', 'Send Questionnaire'];

interface ContextSelectProps {
  contexts: ContextOption[];
  value: string;
  onChange: (context: string) => void;
  disabled?: boolean;
}

export const ContextSelect: React.FC<ContextSelectProps> = ({
  contexts, value, onChange, disabled,
}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, ContextOption[]>();
    contexts.forEach((c) => {
      const group = c.group || FALLBACK_GROUP;
      if (!map.has(group)) map.set(group, []);
      map.get(group)!.push(c);
    });
    map.forEach((items) => items.sort((a, b) => a.label.localeCompare(b.label)));
    return map;
  }, [contexts]);

  const groupNames = useMemo(() => {
    const present = Array.from(grouped.keys());
    const ordered = GROUP_ORDER.filter((g) => present.includes(g));
    return [...ordered, ...present.filter((g) => !ordered.includes(g))];
  }, [grouped]);

  const groupOfValue = useMemo(
    () => contexts.find((c) => c.value === value)?.group || null,
    [contexts, value],
  );

  const [group, setGroup] = useState<string>('');

  // Follow the selected context: opening an existing template has to land on
  // the section that context belongs to, not on whatever was chosen last.
  useEffect(() => {
    if (groupOfValue) setGroup(groupOfValue);
    else if (!group && groupNames.length) setGroup(groupNames[0]);
  }, [groupOfValue, groupNames, group]);

  const options = grouped.get(group) ?? [];

  const handleGroupChange = (nextGroup: string) => {
    setGroup(nextGroup);
    // Moving to another section leaves the old context selected but invisible,
    // which then saves a template under a context the user cannot see. Select
    // the section's first option instead.
    const first = (grouped.get(nextGroup) ?? [])[0];
    if (first && first.value !== value) onChange(first.value);
  };

  return (
    <>
      <Box sx={CLINIC_DIALOG_SX.fieldRow}>
        <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
          Context Type
        </Typography>
        <Select
          value={group}
          onChange={(e) => handleGroupChange(e.target.value as string)}
          fullWidth
          disabled={disabled}
          sx={CLINIC_DIALOG_SX.select}
        >
          {groupNames.map((name) => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box sx={CLINIC_DIALOG_SX.fieldRow}>
        <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>
          Context
        </Typography>
        <Select
          value={options.some((o) => o.value === value) ? value : ''}
          onChange={(e) => onChange(e.target.value as string)}
          fullWidth
          disabled={disabled || options.length === 0}
          displayEmpty
          sx={CLINIC_DIALOG_SX.select}
        >
          {options.length === 0 && (
            <MenuItem value=""><em>Nothing configured in this section</em></MenuItem>
          )}
          {options.map((ctx) => (
            <MenuItem key={ctx.value} value={ctx.value}>
              {ctx.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </>
  );
};

export default ContextSelect;
